import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, CATEGORIES, CATEGORY_NAME } from "@/lib/api";
import { Eye, RefreshCw, Trash2, Plus, ExternalLink, Loader2, CheckCircle2, XCircle, Clock, Download, Target } from "lucide-react";

const STATUS_META = {
  waiting: { label: "Waiting", cls: "text-amber-400 border-amber-700", icon: Clock },
  downloading: { label: "Downloading", cls: "text-sky-400 border-sky-700", icon: Download },
  done: { label: "Ready", cls: "text-emerald-400 border-emerald-700", icon: CheckCircle2 },
  failed: { label: "Failed", cls: "text-rose-400 border-rose-700", icon: XCircle },
  cancelled: { label: "Cancelled", cls: "text-stone-500 border-stone-700", icon: XCircle },
};

export default function Watchlist() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [urls, setUrls] = useState("");
  const [category, setCategory] = useState("");
  const [eventName, setEventName] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [autoAI, setAutoAI] = useState(true);
  const [autoIdentify, setAutoIdentify] = useState(true);
  const [eligibleRiders, setEligibleRiders] = useState([]);
  const [trackRiders, setTrackRiders] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);

  const toggleRider = (name) => {
    setTrackRiders((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const load = async () => {
    try {
      const r = await api.get("/vimeo-watch");
      setEntries(r.data.entries || []);
    } catch {
      toast.error("Failed to load watchlist");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/vimeo-watch/eligible-riders")
      .then((r) => setEligibleRiders(r.data.riders || []))
      .catch(() => {});
    const t = setInterval(load, 15000); // refresh every 15s to catch poller updates
    return () => clearInterval(t);
  }, []);

  const submit = async () => {
    const list = urls
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (list.length === 0) {
      toast.error("Paste at least one Vimeo URL");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/vimeo-watch", {
        urls: list,
        category: category || null,
        event_name: eventName.trim(),
        event_date: eventDate,
        auto_ai_suggest: autoAI,
        auto_identify_riders: autoIdentify,
        track_riders: Array.from(trackRiders),
      });
      const addedN = r.data.added?.length || 0;
      const skippedN = r.data.skipped?.length || 0;
      if (addedN) toast.success(`Added ${addedN} URL${addedN === 1 ? "" : "s"} to the watchlist`);
      if (skippedN) toast.message(`${skippedN} skipped`, {
        description: r.data.skipped.map((s) => `${s.url} — ${s.reason}`).join("\n"),
      });
      setUrls("");
      load();
    } catch (err) {
      toast.error("Failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const checkNow = async (id) => {
    try {
      await api.post(`/vimeo-watch/${id}/check-now`);
      toast.success("Checking Vimeo…");
      setTimeout(load, 2500);
    } catch {
      toast.error("Failed to trigger check");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this entry from the watchlist? (Any already-downloaded video will stay in your Library.)")) return;
    try {
      await api.delete(`/vimeo-watch/${id}`);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Failed to remove");
    }
  };

  return (
    <div className="px-6 md:px-12 py-10 max-w-[1400px]" data-testid="watchlist-page">
      <div className="border-l-4 border-orange-600 pl-5 mb-8">
        <div className="label-tiny mb-2">Watchlist</div>
        <h1 className="font-display uppercase text-5xl font-bold leading-[0.95]">Vimeo Watchlist</h1>
        <p className="text-stone-400 mt-3 max-w-2xl">
          Paste Vimeo links now — before they&apos;ve finished transcoding.
          We ping Vimeo every hour and, the moment a video reaches <span className="text-orange-400">Available</span>,
          auto-download it into your Library and (optionally) queue AI cutting.
        </p>
      </div>

      {/* Add form */}
      <div className="border border-stone-800 bg-stone-950/60 p-5 mb-8" data-testid="watchlist-add-form">
        <div className="label-tiny mb-2 flex items-center gap-2"><Plus size={12} /> Add URLs</div>
        <textarea
          value={urls}
          onChange={(e) => setUrls(e.target.value)}
          placeholder="https://vimeo.com/123456789&#10;https://vimeo.com/987654321"
          rows={3}
          className="field-input font-mono-tc text-xs"
          data-testid="watchlist-urls-input"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <div>
            <label className="label-tiny block mb-1">Category (optional)</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="field-input"
              data-testid="watchlist-category"
            >
              <option value="">— none —</option>
              {CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-tiny block mb-1">Event Name (optional)</label>
            <input
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              placeholder="Olds Open Jackpot"
              className="field-input"
              data-testid="watchlist-event-name"
            />
          </div>
          <div>
            <label className="label-tiny block mb-1">Event Date (optional)</label>
            <input
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              className="field-input"
              data-testid="watchlist-event-date"
            />
          </div>
        </div>
        {eligibleRiders.length > 0 && (
          <div className="mt-4 border border-stone-800 bg-stone-900/40 p-3" data-testid="watchlist-track-riders">
            <div className="flex items-center gap-2 mb-2">
              <Target size={12} className="text-orange-500" />
              <span className="text-[10px] uppercase tracking-widest text-stone-300">
                Auto-track riders <span className="text-stone-500 normal-case">(uses each rider&apos;s saved reference-frame library)</span>
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {eligibleRiders.map((r) => {
                const on = trackRiders.has(r.name);
                return (
                  <button
                    key={r.name}
                    type="button"
                    onClick={() => toggleRider(r.name)}
                    className={`px-2 py-1 text-[11px] border transition-colors inline-flex items-center gap-1 ${
                      on
                        ? "bg-orange-700 border-orange-600 text-white"
                        : "border-stone-700 text-stone-400 hover:border-stone-500"
                    }`}
                    data-testid={`watchlist-track-rider-${r.name.replace(/\s+/g, "-")}`}
                    title={`${r.frame_count} reference frame${r.frame_count === 1 ? "" : "s"} in library`}
                  >
                    {on && <CheckCircle2 size={10} />}
                    {r.name}
                    <span className="font-mono-tc text-[9px] opacity-70">{r.frame_count}</span>
                  </button>
                );
              })}
            </div>
            {trackRiders.size > 0 && (
              <div className="text-[10px] text-orange-400/90 mt-2">
                {trackRiders.size} rider{trackRiders.size === 1 ? "" : "s"} will be auto-tracked once the video downloads.
              </div>
            )}
          </div>
        )}
        {eligibleRiders.length === 0 && (
          <div className="mt-3 text-[10px] text-stone-500 italic">
            No riders have saved reference frames yet. Save frames from a rider profile page to enable auto-tracking here.
          </div>
        )}
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-4 flex-wrap">
            <label className="inline-flex items-center gap-2 text-xs text-stone-300" data-testid="watchlist-auto-ai">
              <input
                type="checkbox"
                checked={autoAI}
                onChange={(e) => setAutoAI(e.target.checked)}
                className="accent-orange-500"
              />
              Auto-cut once downloaded (Deep Scan for &gt;30 min reels, AI Suggest otherwise)
            </label>
            <label className="inline-flex items-center gap-2 text-xs text-stone-300" data-testid="watchlist-auto-identify">
              <input
                type="checkbox"
                checked={autoIdentify}
                onChange={(e) => setAutoIdentify(e.target.checked)}
                className="accent-orange-500"
              />
              Auto-identify riders from announcer audio
            </label>
          </div>
          <button
            onClick={submit}
            disabled={submitting || !urls.trim()}
            className="btn-primary inline-flex items-center gap-2"
            data-testid="watchlist-submit"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
            Add to Watchlist
          </button>
        </div>
        <div className="text-[10px] text-stone-500 mt-2">
          Paste one URL per line, or separate with commas. Only Vimeo URLs are supported. The poller runs every hour;
          click <em>Check now</em> on any entry to poll immediately.
        </div>
      </div>

      {/* List */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-stone-500 font-mono-tc">
          {entries.length} entr{entries.length === 1 ? "y" : "ies"}
        </div>
        <button onClick={load} className="btn-secondary !py-1 !px-2 text-[10px] inline-flex items-center gap-1" data-testid="watchlist-refresh">
          <RefreshCw size={11} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-stone-500 py-16 text-center" data-testid="watchlist-loading">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="border border-dashed border-stone-800 p-16 text-center" data-testid="watchlist-empty">
          <Eye size={32} className="mx-auto text-stone-600 mb-3" />
          <p className="text-stone-400">The watchlist is empty.</p>
          <p className="text-stone-500 text-sm mt-2">Paste a Vimeo URL above to start watching for it.</p>
        </div>
      ) : (
        <div className="border border-stone-800 divide-y divide-stone-800" data-testid="watchlist-list">
          {entries.map((e) => {
            const meta = STATUS_META[e.status] || STATUS_META.waiting;
            const Icon = meta.icon;
            return (
              <div
                key={e.id}
                className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto_auto] gap-3 md:items-center px-4 py-3"
                data-testid={`watchlist-entry-${e.id}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[10px] uppercase tracking-widest ${meta.cls}`} data-testid={`watchlist-status-${e.id}`}>
                      <Icon size={10} /> {meta.label}
                    </span>
                    {e.category && (
                      <span className="text-[10px] text-stone-400 border border-stone-700 px-1.5 py-0.5 uppercase tracking-widest">
                        {CATEGORY_NAME[e.category] || e.category}
                      </span>
                    )}
                    {e.event_name && (
                      <span className="text-xs text-stone-300 truncate max-w-[240px]">{e.event_name}</span>
                    )}
                    {e.event_date && (
                      <span className="text-[10px] text-stone-500 font-mono-tc">{e.event_date}</span>
                    )}
                    {e.auto_ai_suggest && (
                      <span className="text-[10px] text-orange-500/80 border border-orange-800/60 px-1.5 py-0.5 uppercase tracking-widest">
                        + Auto-cut
                      </span>
                    )}
                    {e.auto_identify_riders && (
                      <span className="text-[10px] text-sky-400/90 border border-sky-800/60 px-1.5 py-0.5 uppercase tracking-widest">
                        + Identify
                      </span>
                    )}
                    {e.track_riders && e.track_riders.length > 0 && (
                      <span
                        className="text-[10px] text-emerald-400/90 border border-emerald-800/60 px-1.5 py-0.5 uppercase tracking-widest inline-flex items-center gap-1"
                        title={`Auto-track: ${e.track_riders.join(", ")}`}
                        data-testid={`watchlist-track-badge-${e.id}`}
                      >
                        <Target size={9} /> Track {e.track_riders.length}
                      </span>
                    )}
                  </div>
                  <a
                    href={e.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 text-xs text-stone-300 hover:text-orange-400 font-mono-tc truncate inline-flex items-center gap-1"
                    data-testid={`watchlist-url-${e.id}`}
                  >
                    {e.url} <ExternalLink size={10} />
                  </a>
                  <div className="text-[10px] text-stone-500 font-mono-tc mt-0.5 flex flex-wrap gap-x-3">
                    <span>id · {e.vimeo_id}</span>
                    {e.vimeo_status && <span>vimeo · {e.vimeo_status}</span>}
                    {e.last_checked_at && <span>checked · {new Date(e.last_checked_at).toLocaleString()}</span>}
                    {e.retries > 0 && <span>retries · {e.retries}</span>}
                    {e.note && !e.error && <span className="text-sky-400/80">waiting · {e.note}</span>}
                    {e.error && <span className="text-rose-400">error · {e.error}</span>}
                  </div>
                  {e.status === "downloading" && (
                    <div className="mt-2" data-testid={`watchlist-progress-${e.id}`}>
                      <div className="flex items-center justify-between text-[10px] text-sky-400/90 font-mono-tc mb-1">
                        <span>
                          {e.download_stage
                            ? e.download_stage.replace(/_/g, " ")
                            : "starting"}
                          {typeof e.download_progress === "number" ? ` · ${Math.floor(e.download_progress)}%` : ""}
                        </span>
                        {e.video_status === "failed" && (
                          <span className="text-rose-400">download failed</span>
                        )}
                      </div>
                      <div className="h-1.5 bg-stone-800 overflow-hidden">
                        <div
                          className="h-full bg-sky-500 transition-[width] duration-500"
                          style={{
                            width: `${Math.max(0, Math.min(100, Number(e.download_progress) || 0))}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 justify-end shrink-0">
                  {e.video_id && (e.status === "done" || e.status === "downloading") && (
                    <Link
                      to={`/studio/${e.video_id}`}
                      className="btn-secondary !py-1 !px-2 text-[10px] inline-flex items-center gap-1 hover:!text-orange-400"
                      data-testid={`watchlist-open-studio-${e.id}`}
                    >
                      Open in Studio
                    </Link>
                  )}
                  {e.status !== "done" && e.status !== "failed" && (
                    <button
                      onClick={() => checkNow(e.id)}
                      className="btn-secondary !py-1 !px-2 text-[10px] inline-flex items-center gap-1"
                      data-testid={`watchlist-check-${e.id}`}
                      title="Poll Vimeo API immediately"
                    >
                      <RefreshCw size={11} /> Check now
                    </button>
                  )}
                  {(e.status === "failed" || e.status === "waiting") && (
                    <button
                      onClick={() => checkNow(e.id)}
                      className="btn-secondary !py-1 !px-2 text-[10px] hover:!text-emerald-400"
                      title="Retry"
                      data-testid={`watchlist-retry-${e.id}`}
                    >
                      Retry
                    </button>
                  )}
                  <button
                    onClick={() => remove(e.id)}
                    className="btn-secondary !py-1 !px-2 text-[10px] hover:!text-rose-400"
                    title="Remove from watchlist"
                    data-testid={`watchlist-remove-${e.id}`}
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
